import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import * as PleasedApi from '@/common/api/api.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { storeToRefs } from 'pinia';
import type { UseConnectQueryOptions } from './connectQuery.types';

/**
 * TanStack Query hook to fetch queue position
 *
 * @returns TanStack Query result with queue position data
 *
 * @example
 * ```ts
 * const { data: queuePosition, isLoading } = useQueuePosition();
 * ```
 */
export const useQueuePosition = (options?: UseConnectQueryOptions) => {
  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive, hasConnectData, connectData } = storeToRefs(mockedFlowStore);
  const enabled = computed(() => options?.enabled?.value ?? true);

  return useQuery({
    queryKey: ['queuePosition'],
    queryFn: async () => {
      const fallbackPosition = 1;

      // Return mocked data if available
      if (isMockedFlowActive.value) {
        if (hasConnectData.value && connectData.value) {
          return connectData.value.queuePosition;
        }

        return fallbackPosition;
      }

      const response = await PleasedApi.getQueuePosition().catch(() => {
        return { position: fallbackPosition };
      });

      return response?.position || fallbackPosition;
    },
    enabled,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    refetchInterval: 60 * 1000, // Refetch every 60 seconds
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
  });
};
